import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Shield, Info } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Share = () => {
  const [form, setForm] = useState({
    name: "",
    contact_email: "",
    location: "",
    relationship: "",
    story: "",
    consent_publish: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.story.trim().length < 20) {
      toast.error("Please share your name and a story (at least 20 characters).");
      return;
    }
    if (!form.consent_publish) {
      toast.error("Please confirm you'd like your story to be reviewed for publication.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/stories/submit`, {
        name: form.name.trim(),
        contact_email: form.contact_email.trim() || null,
        location: form.location.trim() || null,
        relationship: form.relationship.trim() || null,
        story: form.story.trim(),
        consent_publish: true,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("We couldn't send your story. Please try again or email haloughazy1997@gmail.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-32 lg:py-40" data-testid="share-thanks">
        <div className="overline text-[#D4AF37]">Thank you</div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#F2EBE5] mt-4 leading-tight">
          Your story has been received.
        </h1>
        <p className="text-[#A19D98] mt-8 leading-relaxed text-lg">
          Ghazy Halou will personally review your submission. Out of respect for
          you and for the historical record, every story is read and considered
          carefully before it is shared on this site.
        </p>
        <p className="text-[#A19D98] mt-4 leading-relaxed">
          If your story is approved, it will appear on the{" "}
          <Link to="/stories" className="text-[#D4AF37] hover:text-[#E5C07B]">
            Survivors page
          </Link>
          . If you provided an email, Ghazy may reach out to confirm details.
        </p>
        <div className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/"
            data-testid="share-back-home"
            className="px-6 py-3 border border-white/20 text-[#F2EBE5] text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-500"
          >
            Return to the memorial
          </Link>
          <Link
            to="/memorial"
            data-testid="share-go-memorial"
            className="px-6 py-3 bg-[#D4AF37] text-black text-sm hover:bg-[#E5C07B] transition-colors duration-500"
          >
            Light a candle
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="share-page">
      <div className="overline text-[#D4AF37]">Add your voice</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EBE5] mt-4 leading-tight">
        Share your story.
      </h1>
      <p className="text-[#A19D98] mt-6 leading-relaxed max-w-2xl">
        If you are a Yazidi survivor, a family member, a humanitarian worker, a
        journalist, or anyone with a personal account from August 2014 or
        after — your voice belongs here.
      </p>

      <div className="border border-white/10 p-6 mt-12 bg-[#111215]" data-testid="share-info">
        <div className="overline mb-3 flex items-center gap-2">
          <Info className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.5} /> How this works
        </div>
        <ul className="text-sm text-[#A19D98] space-y-2 list-disc list-inside leading-relaxed">
          <li>Your story is sent privately to <span className="text-[#F2EBE5]">Ghazy Halou</span>, the site&apos;s maintainer.</li>
          <li>Nothing is published until Ghazy personally reviews and approves it.</li>
          <li>Your email — if you share one — is kept private and never displayed publicly.</li>
          <li>You may use a first name only, or remain anonymous (we&apos;ll display the name you provide as-is).</li>
          <li>You can write in any language; please write in the language you are most comfortable in.</li>
        </ul>
      </div>

      <form onSubmit={submit} className="mt-12 space-y-6" data-testid="share-form">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="overline block mb-2" htmlFor="s-name">Your name</label>
            <input
              id="s-name"
              data-testid="share-input-name"
              value={form.name}
              onChange={onChange("name")}
              maxLength={120}
              className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
              placeholder="A first name, full name, or 'Anonymous'"
            />
          </div>
          <div>
            <label className="overline block mb-2" htmlFor="s-email">
              Email <span className="text-[#6E6B67]">(optional, private)</span>
            </label>
            <input
              id="s-email"
              data-testid="share-input-email"
              type="email"
              value={form.contact_email}
              onChange={onChange("contact_email")}
              maxLength={160}
              className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
              placeholder="So Ghazy may contact you"
            />
          </div>
          <div>
            <label className="overline block mb-2" htmlFor="s-loc">Where you write from <span className="text-[#6E6B67]">(optional)</span></label>
            <input
              id="s-loc"
              data-testid="share-input-location"
              value={form.location}
              onChange={onChange("location")}
              maxLength={120}
              className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
              placeholder="City, country, or camp name"
            />
          </div>
          <div>
            <label className="overline block mb-2" htmlFor="s-rel">Your relationship to Sinjar <span className="text-[#6E6B67]">(optional)</span></label>
            <input
              id="s-rel"
              data-testid="share-input-relationship"
              value={form.relationship}
              onChange={onChange("relationship")}
              maxLength={120}
              className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
              placeholder="Survivor, family member, journalist…"
            />
          </div>
        </div>

        <div>
          <label className="overline block mb-2" htmlFor="s-story">Your story</label>
          <textarea
            id="s-story"
            data-testid="share-input-story"
            value={form.story}
            onChange={onChange("story")}
            minLength={20}
            maxLength={4000}
            rows={10}
            className="w-full bg-transparent border border-white/15 px-4 py-3 text-[#F2EBE5] text-sm leading-relaxed focus:outline-none focus:border-[#D4AF37] transition-colors resize-none font-serif-display"
            placeholder="Tell it the way you want it told. Names, places, dates, feelings — whatever feels right to share."
          />
          <div className="text-[10px] text-[#6E6B67] mt-1 text-right">
            {form.story.length}/4000 {form.story.length < 20 && form.story.length > 0 && "— need at least 20"}
          </div>
        </div>

        <label className="flex items-start gap-3 cursor-pointer" data-testid="share-consent-label">
          <input
            type="checkbox"
            data-testid="share-consent"
            checked={form.consent_publish}
            onChange={onChange("consent_publish")}
            className="mt-1 w-4 h-4 accent-[#D4AF37]"
          />
          <span className="text-sm text-[#A19D98] leading-relaxed">
            I consent to having my story <span className="text-[#F2EBE5]">privately reviewed</span> by
            Ghazy Halou. I understand it will <span className="text-[#F2EBE5]">only be published if approved</span>,
            and that I may withdraw consent at any time by emailing{" "}
            <a href="mailto:haloughazy1997@gmail.com" className="text-[#D4AF37] hover:text-[#E5C07B]">
              haloughazy1997@gmail.com
            </a>.
          </span>
        </label>

        <div className="pt-4 flex flex-wrap items-center gap-4">
          <button
            type="submit"
            disabled={submitting}
            data-testid="share-submit"
            className="px-6 py-3 bg-[#D4AF37] text-black text-sm tracking-wide hover:bg-[#E5C07B] transition-colors duration-500 disabled:opacity-60 inline-flex items-center gap-2"
          >
            <Send className="w-4 h-4" strokeWidth={1.5} />
            {submitting ? "Sending privately…" : "Send privately to Ghazy"}
          </button>
          <div className="text-xs text-[#6E6B67] inline-flex items-center gap-2">
            <Shield className="w-3 h-3" strokeWidth={1.5} /> Reviewed before publishing
          </div>
        </div>
      </form>
    </div>
  );
};

export default Share;
